import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, FlaskConical } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { SecurityHeaders } from '@/components/SecurityHeaders';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { ShippingOption } from '@/lib/shippingConfig';
import CheckoutAuthStep from '@/components/checkout/CheckoutAuthStep';
import CheckoutShippingStep from '@/components/checkout/CheckoutShippingStep';
import { PaymentForm } from '@/components/PaymentForm';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { trackAdInitiateCheckout } from '@/lib/analytics';

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    items,
    selectedItems,
    getSelectedItemsTotal,
    isCheckoutActive,
    setCheckoutActive,
    clearCheckoutState,
    clearCart,
  } = useCart();

  const [currentStep, setCurrentStep] = useState(1);
  const [shippingOption, setShippingOption] = useState<ShippingOption | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | undefined>();
  const [selectedAddressDetails, setSelectedAddressDetails] = useState<any>(null);
  const [shippingCost, setShippingCost] = useState(0);
  const [businessDetails, setBusinessDetails] = useState<{ taxId?: string; businessName?: string; businessAddress?: string } | null>(null);
  const [guestDetails, setGuestDetails] = useState<{ firstName: string; lastName: string; phone: string; email: string } | null>(null);
  
  // Store guest email in sessionStorage for post-payment order verification
  useEffect(() => {
    if (guestDetails?.email) {
      sessionStorage.setItem('checkout_email', guestDetails.email);
    }
  }, [guestDetails]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSkippingPayment, setIsSkippingPayment] = useState(false);

  const selectedCartItems = items.filter(item => selectedItems.has(item.id));

  useEffect(() => {
    setCheckoutActive(true);
    
    // Track InitiateCheckout event when checkout loads with items
    if (selectedCartItems.length > 0) {
      const totalValue = getSelectedItemsTotal();
      trackAdInitiateCheckout(totalValue);
    }
    
    return () => {
      clearCheckoutState();
    };
  }, []);

  useEffect(() => {
    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user?.id) return;

    // Enable for localhost development or local network development
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isLocalIP = window.location.hostname.startsWith('192.168.') ||
      window.location.hostname.startsWith('10.') ||
      window.location.hostname.startsWith('172.');

    if (isLocalHost || isLocalIP) {
      setIsAdmin(true);
      console.log('Admin mode enabled for local network development');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });

      if (!error && data) {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  // Scroll past header on mobile when step changes
  useEffect(() => {
    const isMobile = window.innerWidth < 1024; // lg breakpoint
    const scrollPosition = isMobile ? 10 : 0; // Scroll 10px down on mobile to hide header but keep steps visible
    window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
  }, [currentStep]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleShippingChange = (option: ShippingOption, city: string | null, cost: number, addressId?: string, addressDetails?: any) => {
    setShippingOption(option);
    setSelectedCity(city);
    setShippingCost(cost);
    setSelectedAddressId(addressId);
    if (addressDetails) {
      setSelectedAddressDetails(addressDetails);
    }
  };

  const formatAddress = (address: any) => {
    if (!address) return '';
    const parts = [
      address.street_name && address.street_number ? `${address.street_name} ${address.street_number}` : address.street_name,
      address.city,
      address.details
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleCreateOrder = async (skipPayment: boolean = false) => {
    if (!user || selectedCartItems.length === 0) return null;

    try {
      const subtotal = getSelectedItemsTotal();
      const total = subtotal + shippingCost;

      // Check if any items are group buy
      const hasGroupBuy = selectedCartItems.some((item: any) => item.product.isGroupBuy);

      // Fetch profile for snapshot data
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone, email')
        .eq('id', user.id)
        .single();

      // Prepare billing address with customer details
      const billingAddress = {
        first_name: profile?.first_name || 'Guest',
        last_name: profile?.last_name || '',
        email: profile?.email || user.email,
        phone: profile?.phone || '',
        ...businessDetails // Merge business details if they exist
      };

      // Let the database generate the order number using our function
      const orderData: any = {
        user_id: user.id,
        total_amount: total,
        total_delivery_cost: shippingCost,
        status: skipPayment ? 'processing' : 'pending',
        shipping_option: shippingOption || 'pickup',
        shipping_city: selectedCity,
        order_type: hasGroupBuy ? 'group_buy' : 'regular',
        billing_address: billingAddress
      };

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert(orderData)
        .select()
        .single();

      if (orderError) throw orderError;

      // Create sub-orders and update group buy quantities
      // Map ShippingOption to database delivery_option enum
      const mapDeliveryOption = (option: ShippingOption | null): 'delivery_only' | 'pickup_only' | 'both' => {
        if (!option || option === 'pickup') return 'pickup_only';
        if (option === 'delivery' || option === 'crane') return 'delivery_only';
        return 'both';
      };

      const subOrdersData = selectedCartItems.map(item => ({
        order_id: order.id,
        product_id: item.product.id,
        product_name: item.product.name,
        product_description: item.product.name, // Use name as Listing doesn't have description
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.unitPrice * item.quantity,
        delivery_option: mapDeliveryOption(shippingOption),
        delivery_cost: shippingCost / selectedCartItems.length,
        delivery_address: shippingOption === 'delivery' ? formatAddress(selectedAddressDetails) : null,
        status: skipPayment ? 'processing' as const : 'pending' as const,
      }));

      const { error: subOrdersError } = await supabase
        .from('sub_orders')
        .insert(subOrdersData);

      if (subOrdersError) throw subOrdersError;

      // Update group buy quantities for group buy products
      // Inventory update is now handled by database triggers on order creation/status update
      // We only need to check group buy goals for relevant items
      for (const item of selectedCartItems) {
        try {
          // Check Group Buy Goal if enabled
          if (item.product.group_buy_enabled && item.product.id) {
            // We invoke the check goal logic - the quantity is updated by DB trigger
            // giving it a moment to propagate isn't guaranteed here, but the check-goal 
            // function usually calculates total from orders anyway.
            await supabase.functions.invoke('check-group-buy-goal', {
              body: { product_id: item.product.id }
            });
          }
        } catch (error) {
          console.error('Error triggering group buy goal check:', error);
        }
      }

      return order.id;
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error("שגיאה ביצירת הזמנה", {
        description: "אירעה שגיאה ביצירת ההזמנה",
      });
      return null;
    }
  };

  const handleSkipPayment = async () => {
    setIsSkippingPayment(true);

    try {
      const orderId = await handleCreateOrder(true);

      if (orderId) {
        toast.success("הזמנת טסט נוצרה בהצלחה", {
          description: "ההזמנה נוצרה ללא תשלום למטרות בדיקה",
        });

        // Send order confirmation emails
        try {
          console.log('Invoking send-order-confirmation for order:', orderId);
          const { error: invokeError } = await supabase.functions.invoke('send-order-confirmation', {
            body: { orderId }
          });

          if (invokeError) {
            console.error('Invoke returned error:', invokeError);
            toast.error("שגיאה בשליחת המייל", {
              description: `פרטי השגיאה: ${invokeError.message || JSON.stringify(invokeError)}`
            });
          } else {
            console.log('Order confirmation emails sent successfully');
            toast.success("מיילים נשלחו", { description: "אישור הזמנה נשלח בהצלחה" });
          }
        } catch (emailError) {
          console.error('Error invoking confirmation emails:', emailError);
          // Don't block the flow if email fails
        }

        clearCart();
        navigate(`/payment/success?orderId=${orderId}&source=admin-test`);
      }
    } catch (error) {
      console.error('Error skipping payment:', error);
      toast.error("שגיאה", {
        description: "אירעה שגיאה ביצירת הזמנת הטסט",
      });
    } finally {
      setIsSkippingPayment(false);
    }
  };

  const subtotal = getSelectedItemsTotal();
  const total = subtotal + shippingCost;

  const stepLabels = ['פרטים', 'משלוח', 'תשלום'];

  if (selectedCartItems.length === 0) {
    return (
      <>
        <SecurityHeaders />
        <Navigation />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4">העגלה ריקה</h1>
              <Button onClick={() => navigate('/products')}>חזור לקניות</Button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SecurityHeaders />
      <Navigation />
      <div className="min-h-screen bg-background pt-0 lg:pt-20">
        <div className="container mx-auto px-4 py-4 lg:py-8">
          {/* Progress Stepper */}
          <div className="max-w-4xl mx-auto mb-8 px-4">
            <div className="flex items-center justify-center md:justify-between" dir="rtl">
              {stepLabels.map((label, index) => {
                const stepNum = index + 1;
                const isActive = currentStep === stepNum;
                const isCompleted = currentStep > stepNum;

                return (
                  <div key={stepNum} className="flex items-center md:flex-1">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors text-sm md:text-base ${isCompleted ? 'bg-lime text-lime-foreground' :
                        isActive ? 'bg-primary text-primary-foreground' :
                          'bg-muted border-2 border-border text-foreground'
                        }`}>
                        {isCompleted ? <Check className="h-4 w-4 md:h-5 md:w-5" /> : stepNum}
                      </div>
                      <span className={`text-xs md:text-sm mt-1 md:mt-2 whitespace-nowrap ${isActive ? 'font-semibold' : 'text-muted-foreground'
                        }`}>
                        {label}
                      </span>
                    </div>
                    {index < stepLabels.length - 1 && (
                      <div className={`h-0.5 md:h-1 w-8 mx-2 md:mx-4 md:flex-1 transition-colors ${isCompleted ? 'bg-lime' : 'bg-border'
                        }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {currentStep === 1 && (
                <CheckoutAuthStep
                  onNext={(businessDetails, personalDetails) => {
                    if (businessDetails) setBusinessDetails(businessDetails);
                    if (personalDetails) setGuestDetails(personalDetails);
                    setCurrentStep(2);
                  }}
                  onBack={() => navigate('/cart')}
                  initialData={guestDetails ? { ...guestDetails, ...businessDetails } : null}
                />
              )}

              {currentStep === 2 && (
                <CheckoutShippingStep
                  onNext={() => setCurrentStep(3)}
                  onBack={() => setCurrentStep(1)}
                  onShippingChange={handleShippingChange}
                  selectedOption={shippingOption}
                  selectedCity={selectedCity}
                  selectedAddressId={selectedAddressId}
                  subtotal={subtotal}
                />
              )}

              {currentStep === 3 && (
                <div className="space-y-6" dir="rtl">
                  {isAdmin && (
                    <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                      <CardContent className="pt-6 px-4 md:px-6">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <FlaskConical className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-amber-900 dark:text-amber-100">מצב בדיקה - אדמין בלבד</p>
                              <p className="text-sm text-amber-700 dark:text-amber-300">דלג על תהליך התשלום ליצירת הזמנת טסט</p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            className="border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white w-full md:w-auto"
                            disabled={isSkippingPayment}
                            onClick={handleSkipPayment}
                          >
                            {isSkippingPayment ? 'יוצר הזמנה...' : 'דלג על תשלום'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <PaymentForm
                    amount={total}
                    orderData={{
                      checkoutItems: selectedCartItems,
                      deliveryChoices: selectedCartItems.reduce((acc, item) => ({
                        ...acc,
                        [item.id]: shippingOption || 'pickup'
                      }), {}),
                      deliveryCosts: selectedCartItems.reduce((acc, item) => ({
                        ...acc,
                        [item.id]: shippingCost / selectedCartItems.length
                      }), {}),
                      selectedShippingAddress: selectedAddressDetails || selectedAddressId,
                      selectedBillingAddress: businessDetails,
                      agreeTo: true,
                      formData: guestDetails // Pass guest details to PaymentForm
                    }}
                    isActive={true} // Add dummy prop if needed or just isGuest
                    isGuest={!user}
                    onSuccess={async (orderId) => {
                      // console.log('Payment successful:', orderId);
                      clearCart();
                      navigate(`/payment/success?orderId=${orderId}&source=checkout`);
                    }}
                    onError={() => {
                      console.error('Payment failed');
                      // Don't navigate away, let the user retry or see the error
                      // navigate('/payment/cancel'); 
                    }}
                    onBack={() => setCurrentStep(2)}
                  />
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="text-right">סיכום הזמנה</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4" dir="rtl">
                  <div className="flex justify-between">
                    <span>סה"כ מוצרים:</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>

                  {shippingOption && (
                    <>
                      <div className="flex justify-between">
                        <span>משלוח:</span>
                        <span>{shippingOption === 'pickup' ? 'חינם' : formatPrice(shippingCost)}</span>
                      </div>
                      {shippingOption === 'delivery' && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          ⚠️ פריקה על שפת המדרכה בלבד
                        </p>
                      )}
                      <Separator />
                    </>
                  )}

                  <div className="flex justify-between text-xl font-bold">
                    <span>סה"כ לתשלום:</span>
                    <div className="text-left">
                      <span className="text-lime">{formatPrice(total)}</span>
                      <span className="block text-xs text-muted-foreground font-normal">כולל מע"מ</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Checkout;
